import "./heatmap.css";
import React from 'react';
import { coinIconUrls, stockLogoUrls } from '../constants';

const getHeatmapColor = (change) => {
  if (change === null || change === undefined) return 'rgba(212, 168, 67, 0.16)';
  if (change >= 10) return 'rgba(34, 197, 94, 0.88)';
  if (change >= 5) return 'rgba(34, 197, 94, 0.68)';
  if (change >= 2) return 'rgba(34, 197, 94, 0.48)';
  if (change >= 0) return 'rgba(34, 197, 94, 0.28)';
  if (change >= -2) return 'rgba(248, 113, 113, 0.42)';
  if (change >= -5) return 'rgba(248, 113, 113, 0.68)';
  if (change >= -10) return 'rgba(239, 68, 68, 0.78)';
  return 'rgba(220, 38, 38, 0.9)';
};

const formatHeatmapChange = (change, t) => {
  if (change === null || change === undefined) return t('home.heatmapFeatured');
  return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
};

const formatHeatmapMeta = (weight) => {
  if (!weight || weight < 1) return '';
  if (weight >= 1e12) return `$${(weight / 1e12).toFixed(1)}T`;
  if (weight >= 1e9) return `$${(weight / 1e9).toFixed(0)}B`;
  if (weight >= 1e6) return `$${(weight / 1e6).toFixed(0)}M`;
  return `${weight.toFixed(1)}x`;
};

const buildHeatmapPreviewGroups = (heatmapData, stockQuotes, t) => {
  const coinMap = new Map((heatmapData || []).map((coin) => [coin.id, coin]));
  const cryptoIds = ['bitcoin', 'ethereum', 'solana', 'binancecoin', 'ripple', 'dogecoin', 'cardano', 'avalanche-2', 'pax-gold'];
  const cryptoAssets = cryptoIds
    .map((id) => {
      const coin = coinMap.get(id);
      if (!coin) return null;
      return {
        id: coin.id,
        symbol: coin.symbol?.toUpperCase(),
        name: coin.name,
        change: coin.price_change_percentage_24h,
        weight: coin.market_cap || 1,
        image: coin.image || coinIconUrls[coin.id],
      };
    })
    .filter(Boolean);

  const stockAssets = [
    { symbol: 'NVDA', name: 'NVIDIA', fallbackWeight: 3.1, fallbackChange: 1.8 },
    { symbol: 'MSTR', name: 'MicroStrategy', fallbackWeight: 1.4, fallbackChange: 7.4 },
    { symbol: 'COIN', name: 'Coinbase', fallbackWeight: 1.2, fallbackChange: -3.2 },
    { symbol: 'QQQ', name: 'Nasdaq 100 ETF', fallbackWeight: 1.8, fallbackChange: -0.4 },
    { symbol: 'TSLA', name: 'Tesla', fallbackWeight: 0.95, fallbackChange: 2.6 },
    { symbol: 'AAPL', name: 'Apple', fallbackWeight: 2.9, fallbackChange: 0.7 },
  ].map((stock) => {
    const quote = stockQuotes[stock.symbol];
    return {
      id: stock.symbol.toLowerCase(),
      symbol: stock.symbol,
      name: quote?.shortName || quote?.longName || stock.name,
      change: typeof quote?.regularMarketChangePercent === 'number'
        ? quote.regularMarketChangePercent
        : stock.fallbackChange,
      weight: quote?.marketCap || stock.fallbackWeight,
      image: stockLogoUrls[stock.symbol],
    };
  });

  return [
    {
      key: 'crypto',
      label: t('home.heatmapCryptoTitle'),
      desc: t('home.heatmapCryptoDesc'),
      assets: cryptoAssets,
    },
    {
      key: 'stocks',
      label: t('home.heatmapStocksTitle'),
      desc: t('home.heatmapStocksDesc'),
      assets: stockAssets,
    },
  ];
};

const HeatmapPanel = ({ heatmapData, heatmapLoading, stockQuotes, navigate, t }) => {
  if (heatmapLoading) return (
    <div className="hp-pro-card">
      <div className="hp-pro-card-title">{t('home.heatmapTitle')}</div>
      <div className="hp-heatmap-loading">{t('home.heatmapLoading')}</div>
    </div>
  );

  if (!heatmapData) return (
    <div className="hp-pro-card">
      <div className="hp-pro-card-title">{t('home.heatmapTitle')}</div>
      <div className="hp-heatmap-loading">{t('home.heatmapError')}</div>
    </div>
  );

  const previewGroups = buildHeatmapPreviewGroups(heatmapData, stockQuotes, t);

  return (
    <div className="hp-pro-card">
      <div className="hp-heatmap-title-row">
        <div className="hp-pro-card-title">{t('home.heatmapTitle')}</div>
        <button className="hp-heatmap-more" onClick={() => navigate('/market-heatmap')}>
          {t('home.heatmapViewFull')} →
        </button>
      </div>
      <div className="hp-heatmap-wrap">
        {previewGroups.map((group) => (
          <div key={group.key} className={`hp-heatmap-sector hp-heatmap-sector-${group.key}`}>
            <div className="hp-heatmap-sector-header">
              <div>
                <span className="hp-heatmap-sector-name">{group.label}</span>
                <span className="hp-heatmap-sector-desc">{group.desc}</span>
              </div>
            </div>
            <div className="hp-heatmap-blocks hp-heatmap-lane">
              {group.assets.map((asset) => {
                return (
                  <div
                    key={asset.id}
                    className="hp-heatmap-block"
                    style={{
                      backgroundColor: getHeatmapColor(asset.change),
                    }}
                    title={`${asset.name}: ${formatHeatmapChange(asset.change, t)}`}
                  >
                    {asset.image && (
                      <span className="hp-heatmap-token-icon-wrap" data-symbol={asset.symbol}>
                        <img
                          className="hp-heatmap-token-icon"
                          src={asset.image}
                          alt=""
                          loading="lazy"
                          onError={(event) => {
                            event.currentTarget.style.display = 'none';
                            event.currentTarget.parentElement?.classList.add('is-fallback');
                          }}
                        />
                      </span>
                    )}
                    <span className="hp-heatmap-block-title">
                      <span className="hp-heatmap-block-symbol">{asset.symbol}</span>
                      <span className="hp-heatmap-block-name">{asset.name}</span>
                    </span>
                    {typeof asset.change === 'number' && (
                      <span className="hp-heatmap-block-change">{formatHeatmapChange(asset.change, t)}</span>
                    )}
                    {formatHeatmapMeta(asset.weight) && (
                      <span className="hp-heatmap-block-meta">{formatHeatmapMeta(asset.weight)}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HeatmapPanel;
